import { Link, useParams, useLocation } from 'react-router-dom';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { NavItem } from '@/components/navigation/NavItem';
import { Icons } from '@/lib/icons';
import { cn } from '@/lib/utils';

export function DashboardSidebar({ className }: { className?: string }) {
  const { id: workspaceId } = useParams<{ id: string }>();
  const location = useLocation();
  const pathname = location.pathname;
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const workspace = workspaces.find((w) => w.id === workspaceId);

  const isTickets = pathname?.includes('/tickets') ?? false;
  const isKnowledgeBase = pathname?.includes('/knowledge-base') ?? false;
  const isProjects =
    pathname?.includes('/projects') || pathname?.includes('/settings/projects') || false;

  if (!workspace) return null;

  const navItems = [
    {
      href: `/workspace/${workspaceId}/tickets`,
      label: 'Tickets',
      icon: Icons.Ticket,
      active: isTickets
    },
    {
      href: `/workspace/${workspaceId}/knowledge-base`,
      label: 'Knowledge Base',
      icon: Icons.AcademicCap,
      active: isKnowledgeBase
    }
  ];

  return (
    <div className={cn('flex h-full w-56 flex-col overflow-y-auto pt-3', className)}>
      {/* Workspace switcher */}
      <div className="shrink-0 px-2 pb-2">
        <DropdownMenu>
          <DropdownMenuTrigger>
            <div className="hover:bg-muted flex w-full items-center gap-2 rounded-md p-1">
              <div className="bg-muted rounded-xs h-5.5 w-5.5" />
              <span className="text-muted-foreground truncate text-sm font-semibold tracking-tight">
                {workspace.name}
              </span>
              <MonoIcon type={Icons.ChevronDown} className="text-muted-foreground ml-auto" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
            {workspaces.map((w) => (
              <DropdownMenuItem key={w.id}>
                <Link
                  to={w.id === workspaceId ? `/workspace/${workspaceId}` : `/workspace/${w.id}`}
                  className={cn('block truncate', w.id === workspaceId && 'bg-muted font-medium')}
                >
                  {w.name}
                </Link>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <Link to="/create-workspace" className="block">
                Create workspace
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-0.5 px-2">
        {navItems.map((item) => (
          <NavItem
            key={item.href}
            to={item.href}
            label={item.label}
            icon={item.icon}
            active={item.active}
          />
        ))}
      </nav>

      {/* Settings (icon only) + User + Sign out */}
      <div className="border-border flex shrink-0 flex-col gap-1 border-t p-2">
        <Link
          to={`/workspace/${workspaceId}/settings`}
          className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }))}
          title="Settings"
          aria-label="Settings"
        >
          <MonoIcon type={Icons.Settings} className="text-muted-foreground" />
        </Link>
      </div>
    </div>
  );
}
